import { useRef } from 'react';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import AddIcon from '@mui/icons-material/Add';

interface DestinationsInputProps {
  value: string[];
  onChange: (destinations: string[]) => void;
  error?: string;
  inputRef?: React.RefObject<HTMLInputElement | null>;
}

export function DestinationsInput({ value, onChange, error, inputRef: externalRef }: DestinationsInputProps) {
  const internalRef = useRef<HTMLInputElement>(null);
  const inputRef = externalRef ?? internalRef;

  const addDestination = (raw: string) => {
    const trimmed = raw.trim();
    if (!trimmed || value.includes(trimmed)) return false;
    onChange([...value, trimmed]);
    return true;
  };

  const commitInput = () => {
    const input = inputRef.current;
    if (!input) return;
    if (addDestination(input.value)) {
      input.value = '';
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      commitInput();
    }
  };

  const removeDestination = (dest: string) => {
    onChange(value.filter((d) => d !== dest));
  };

  return (
    <Box>
      <Typography variant="body2" fontWeight={500} sx={{ mb: 1 }}>
        Destinations Served
        <Box component="span" sx={{ color: 'error.main', ml: 0.3 }}>*</Box>
      </Typography>

      <TextField
        inputRef={inputRef}
        fullWidth
        label="Add a destination"
        placeholder="e.g. Agra, Hampi, Leh — press Enter or tap +"
        onKeyDown={handleKeyDown}
        error={!!error}
        helperText={error || 'Type a destination, then press Enter or click + to add it'}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="Add destination" onClick={commitInput} edge="end" size="small">
                <AddIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      {value.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
          {value.map((dest) => (
            <Box
              key={dest}
              component="button"
              type="button"
              onClick={() => removeDestination(dest)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                border: 'none',
                cursor: 'pointer',
                bgcolor: 'secondary.50',
                borderRadius: 'var(--radius-sm)',
                px: 1.5,
                py: 0.5,
                fontSize: '0.8125rem',
                fontWeight: 500,
                color: 'secondary.dark',
                '&:hover': { bgcolor: 'primary.100', color: 'primary.main' },
              }}
            >
              {dest} ×
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

/** Returns destinations including any uncommitted text still in the input. */
export function flushDestinationsInput(
  inputRef: React.RefObject<HTMLInputElement | null>,
  current: string[]
): string[] {
  const pending = inputRef.current?.value.trim();
  if (!pending || current.includes(pending)) return current;
  return [...current, pending];
}
